import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, LoginResponse } from '../../shared/models/auth';
import { User } from '../../shared/models/user';

const TOKEN_KEY = 'prenda_token';

// .NET ClaimTypes.Role se serializa con este key en el JWT
const ROLE_CLAIM = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';

// El codeudor y el cónyuge entran a la misma sección que el deudor: son participantes del
// trámite como él, y el backend ya le muestra a cada uno solo lo suyo. Antes no figuraban acá
// —el backend los llama "CoDeudor" y "Conyuge", no "Cosigner"— y no podían entrar a ninguna.
const ROLE_TO_ROUTE: Record<string, string> = {
  Acreedor:            'lender',
  Concesionaria:       'dealer',
  Deudor:              'debtor',
  CoDeudor:            'debtor',
  Conyuge:             'debtor',
  Cosigner:            'cosigner',
  SuperAdmin:          'lender',
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http   = inject(HttpClient);
  private router = inject(Router);

  login(email: string, password: string) {
    return this.http
      .post<ApiResponse<LoginResponse>>(`${environment.apiUrl}/auth/login`, { email, password })
      .pipe(tap(res => localStorage.setItem(TOKEN_KEY, res.response.token)));
  }

  activateAccount(token: string, password: string) {
    return this.http.post<ApiResponse<null>>(`${environment.apiUrl}/auth/activate`, { token, password });
  }

  /** "¿Olvidaste tu contraseña?". Responde siempre lo mismo, exista o no el correo. */
  requestPasswordReset(email: string) {
    return this.http.post<ApiResponse<null>>(`${environment.apiUrl}/auth/forgot-password`, { email });
  }

  /** Si el link para restablecer todavía sirve (410 si venció o ya se usó). No lo gasta. */
  validatePasswordResetLink(token: string) {
    return this.http.post<ApiResponse<null>>(`${environment.apiUrl}/auth/reset-password/validate`, { token });
  }

  /** Define la contraseña nueva con el link del correo (410 si el link ya no sirve). */
  resetPassword(token: string, password: string) {
    return this.http.post<ApiResponse<null>>(`${environment.apiUrl}/auth/reset-password`, { token, password });
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    this.router.navigateByUrl('/login');
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    try {
      const { exp } = this.decodePayload(token);
      return exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  getCurrentUser(): User | null {
    const token = this.getToken();
    if (!token) return null;
    try {
      const payload = this.decodePayload(token);
      return {
        id:          payload['sub'],
        email:       payload['email'],
        name:        payload['name'] ?? '',
        firstName:   payload['given_name'] ?? '',
        cuil:        payload['cuil'],
        role:        payload[ROLE_CLAIM] ?? '',
        tenantId:    payload['tenant_id'],
        tenantName:  payload['tenant_name'] ?? '',
        isSuperAdmin: payload['is_super_admin'] === 'true',
        canManageUsers: payload['can_manage_users'] === 'true',
      };
    } catch {
      return null;
    }
  }

  getRouteForRole(role: string): string {
    return ROLE_TO_ROUTE[role] ?? 'login';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private decodePayload(token: string): any {
    const base64Url = token.split('.')[1];
    const base64    = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  }
}
